/**
 * <pickup-scheduler>
 *
 * Cart-attribute-backed pickup date + comment form with a custom inline
 * calendar (replaces the unreliable native <input type="date"> picker).
 *
 * - Date is mandatory; checkout is blocked until a valid date is picked.
 * - "Now" is computed in the shop's timezone (Australia/Melbourne) so the
 *   12pm cutoff rule is anchored to bakery hours, not the customer's clock.
 * - Closed weekdays (Sun, Mon) and merchant-supplied blackout dates are
 *   visually disabled in the calendar grid.
 * - Values persist across cart morphs because they're stored in
 *   cart.attributes and re-hydrated from data-saved-* on every
 *   connectedCallback.
 *
 * Date math: all calendar arithmetic uses Date.UTC plus getUTC/setUTC
 * helpers to avoid local-time DST jumps shifting a date by one day.
 * The shop-timezone "now" function uses Intl.DateTimeFormat which
 * handles DST natively.
 */

const MAX_MONTHS_AHEAD = 3;

class PickupScheduler extends HTMLElement {
  #commentTimer = null;
  #checkoutGuard = null;
  #viewMonth = null; // { year, month } — month currently displayed (1-12)
  #earliestYMD = null; // YYYY-MM-DD string, earliest valid pickup date
  #todayYMD = null; // YYYY-MM-DD in shop timezone
  #weekdayLabels = null;

  connectedCallback() {
    this.dateInput = this.querySelector('#pickup-date');
    this.commentInput = this.querySelector('#pickup-comment');
    this.errorEl = this.querySelector('[data-pickup-error]');
    this.calendarEl = this.querySelector('[data-pickup-calendar]');

    this.shopTimezone = this.dataset.shopTimezone || 'Australia/Melbourne';
    this.cutoffHour = parseInt(this.dataset.cutoffHour, 10);
    if (Number.isNaN(this.cutoffHour)) this.cutoffHour = 12;

    this.closedWeekdays = (this.dataset.closedWeekdays || '0,1')
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));

    this.blackoutDates = (this.dataset.blackoutDates || '')
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));

    this.messages = {
      selectDate: this.dataset.msgSelectDate,
      tooSoon: this.dataset.msgTooSoon,
      closedWeekday: this.dataset.msgClosedWeekday,
      blackout: this.dataset.msgBlackout,
    };

    const now = this.#nowInShopTimezone();
    this.#todayYMD = this.#formatYMD(new Date(Date.UTC(now.year, now.month - 1, now.day)));
    this.#earliestYMD = this.#computeEarliestDate(now);
    this.dateInput.min = this.#earliestYMD;

    if (this.dataset.savedDate) this.dateInput.value = this.dataset.savedDate;
    if (this.dataset.savedComment) this.commentInput.value = this.dataset.savedComment;

    // Initial view: month containing saved-date (if any & in future) or earliest.
    const initialAnchor = this.dateInput.value && this.dateInput.value >= this.#earliestYMD
      ? this.dateInput.value
      : this.#earliestYMD;
    const anchor = this.#parseYMD(initialAnchor);
    this.#viewMonth = { year: anchor.getUTCFullYear(), month: anchor.getUTCMonth() + 1 };

    this.#weekdayLabels = this.#buildWeekdayLabels();
    this.#renderCalendar();

    this.dateInput.addEventListener('input', this.#onDateChange);
    this.commentInput.addEventListener('input', this.#onCommentInput);

    this.#checkoutGuard = (event) => {
      if (!this.#validate()) {
        event.preventDefault();
        event.stopPropagation();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    this.#initialiseCheckoutGate();
  }

  #initialiseCheckoutGate() {
    const attach = () => {
      this.#checkoutButtons().forEach((btn) => {
        btn.addEventListener('click', this.#checkoutGuard, { capture: true });
      });
      this.#validate();
    };

    requestAnimationFrame(attach);
    setTimeout(attach, 250);
  }

  disconnectedCallback() {
    if (this.#checkoutGuard) {
      this.#checkoutButtons().forEach((btn) => {
        btn.removeEventListener('click', this.#checkoutGuard, { capture: true });
      });
    }
    if (this.#commentTimer) clearTimeout(this.#commentTimer);
  }

  #checkoutButtons() {
    return document.querySelectorAll('.cart__checkout-button');
  }

  #onDateChange = () => {
    if (!this.#validate()) return;
    this.#persist({ 'Pickup Date': this.dateInput.value });
  };

  #onCommentInput = () => {
    if (this.#commentTimer) clearTimeout(this.#commentTimer);
    this.#commentTimer = setTimeout(() => {
      this.#persist({ 'Pickup Comment': this.commentInput.value });
    }, 400);
  };

  async #persist(attributes) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ attributes }),
      });
    } catch {
      // Non-fatal — the customer can retry on checkout click.
    }
  }

  // ─── Calendar rendering ─────────────────────────────────────────────────

  #renderCalendar() {
    if (!this.calendarEl) return;

    const { year, month } = this.#viewMonth;
    const monthLabel = new Intl.DateTimeFormat(undefined, {
      month: 'long',
      year: 'numeric',
    }).format(new Date(Date.UTC(year, month - 1, 1)));

    const canGoPrev = this.#canNavigate(-1);
    const canGoNext = this.#canNavigate(1);

    const days = this.#buildMonthGrid(year, month);

    const weekdayMarkup = this.#weekdayLabels
      .map((w) => `<span class="pickup-calendar__weekday">${w}</span>`)
      .join('');

    const dayMarkup = days
      .map((d) => {
        const classes = ['pickup-calendar__day'];
        if (!d.inMonth) classes.push('pickup-calendar__day--outside');
        const attrs = [`aria-label="${this.#escapeAttr(this.#dayAriaLabel(d))}"`];
        if (d.disabled) attrs.push('disabled');
        if (d.selected) attrs.push('aria-pressed="true"');
        if (d.isToday) attrs.push('aria-current="date"');
        return (
          `<button type="button" class="${classes.join(' ')}" ` +
          `data-date="${d.ymd}" ${attrs.join(' ')}>` +
          `${d.day}</button>`
        );
      })
      .join('');

    this.calendarEl.innerHTML = `
      <div class="pickup-calendar__header">
        <button type="button" class="pickup-calendar__nav" data-pickup-nav="prev"
          aria-label="Previous month" ${canGoPrev ? '' : 'disabled'}>‹</button>
        <span class="pickup-calendar__month-label" aria-live="polite">${monthLabel}</span>
        <button type="button" class="pickup-calendar__nav" data-pickup-nav="next"
          aria-label="Next month" ${canGoNext ? '' : 'disabled'}>›</button>
      </div>
      <div class="pickup-calendar__weekdays" aria-hidden="true">${weekdayMarkup}</div>
      <div class="pickup-calendar__grid" role="grid">${dayMarkup}</div>
    `;

    this.calendarEl.querySelectorAll('[data-pickup-nav]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const dir = btn.dataset.pickupNav === 'prev' ? -1 : 1;
        this.#navigate(dir);
      });
    });

    this.calendarEl.querySelectorAll('[data-date]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        this.#onDayClick(btn.dataset.date);
      });
    });
  }

  #escapeAttr(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  #buildMonthGrid(year, month) {
    // Month is 1-indexed; Date.UTC takes 0-indexed.
    const firstOfMonth = new Date(Date.UTC(year, month - 1, 1));
    // Convert JS day-of-week (0=Sun..6=Sat) to Monday-aligned offset (0=Mon..6=Sun).
    const firstWeekday = (firstOfMonth.getUTCDay() + 6) % 7;
    const gridStart = new Date(firstOfMonth);
    gridStart.setUTCDate(gridStart.getUTCDate() - firstWeekday);

    const selected = this.dateInput.value;
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setUTCDate(d.getUTCDate() + i);
      const ymd = this.#formatYMD(d);
      const inMonth = d.getUTCMonth() + 1 === month;
      const tooEarly = ymd < this.#earliestYMD;
      const closed = this.closedWeekdays.includes(d.getUTCDay());
      const blackout = this.blackoutDates.includes(ymd);
      cells.push({
        ymd,
        day: d.getUTCDate(),
        inMonth,
        disabled: !inMonth || tooEarly || closed || blackout,
        selected: ymd === selected,
        isToday: ymd === this.#todayYMD,
      });
    }
    return cells;
  }

  #buildWeekdayLabels() {
    // Monday-aligned. Use a known-Monday reference date (2024-01-01 was a Monday).
    const labels = [];
    const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
    for (let i = 0; i < 7; i++) {
      labels.push(fmt.format(new Date(Date.UTC(2024, 0, 1 + i))));
    }
    return labels;
  }

  #dayAriaLabel(d) {
    const date = this.#parseYMD(d.ymd);
    const formatted = new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);
    if (d.disabled && d.inMonth) return `${formatted} — unavailable`;
    return formatted;
  }

  #canNavigate(direction) {
    const next = this.#shiftMonth(this.#viewMonth, direction);
    const earliest = this.#parseYMD(this.#earliestYMD);
    const earliestMonth = { year: earliest.getUTCFullYear(), month: earliest.getUTCMonth() + 1 };

    if (direction < 0) {
      return this.#compareMonths(next, earliestMonth) >= 0;
    }
    const maxMonth = this.#shiftMonth(earliestMonth, MAX_MONTHS_AHEAD);
    return this.#compareMonths(next, maxMonth) <= 0;
  }

  #navigate(direction) {
    if (!this.#canNavigate(direction)) return;
    this.#viewMonth = this.#shiftMonth(this.#viewMonth, direction);
    // Defer the re-render so the click event finishes bubbling before the
    // chevron button is removed from the DOM. Otherwise the cart drawer's
    // click-outside detector sees a detached event.target and dismisses.
    requestAnimationFrame(() => this.#renderCalendar());
  }

  #shiftMonth({ year, month }, delta) {
    const m0 = month - 1 + delta;
    return {
      year: year + Math.floor(m0 / 12),
      month: ((m0 % 12) + 12) % 12 + 1,
    };
  }

  #compareMonths(a, b) {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  }

  #onDayClick(ymd) {
    this.dateInput.value = ymd;

    // Update the selected highlight surgically rather than rebuilding the
    // grid via innerHTML. Replacing innerHTML would remove the clicked
    // button from the DOM before the click event finishes bubbling, which
    // makes the cart drawer's click-outside detector think the click
    // landed outside the dialog and dismiss it.
    this.calendarEl
      .querySelectorAll('[aria-pressed="true"]')
      .forEach((el) => el.removeAttribute('aria-pressed'));
    const next = this.calendarEl.querySelector(`[data-date="${ymd}"]`);
    if (next) next.setAttribute('aria-pressed', 'true');

    this.dateInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // ─── Validation, errors, checkout gate ──────────────────────────────────

  #validate() {
    const value = this.dateInput.value;

    if (!value) {
      this.#showError(this.messages.selectDate);
      this.#setCheckoutEnabled(false);
      return false;
    }

    const min = this.dateInput.min;
    if (min && value < min) {
      this.#showError(this.messages.tooSoon.replace('{date}', this.#humanizeDate(min)));
      this.#setCheckoutEnabled(false);
      return false;
    }

    const parsed = this.#parseYMD(value);
    if (!parsed) {
      this.#showError(this.messages.selectDate);
      this.#setCheckoutEnabled(false);
      return false;
    }

    if (this.closedWeekdays.includes(parsed.getUTCDay())) {
      this.#showError(this.messages.closedWeekday);
      this.#setCheckoutEnabled(false);
      return false;
    }

    if (this.blackoutDates.includes(value)) {
      this.#showError(this.messages.blackout);
      this.#setCheckoutEnabled(false);
      return false;
    }

    this.#clearError();
    this.#setCheckoutEnabled(true);
    return true;
  }

  #showError(msg) {
    this.errorEl.textContent = msg || '';
    this.errorEl.hidden = !msg;
    this.toggleAttribute('data-invalid', Boolean(msg));
  }

  #clearError() {
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
    this.removeAttribute('data-invalid');
  }

  #setCheckoutEnabled(enabled) {
    this.#checkoutButtons().forEach((btn) => {
      btn.disabled = !enabled;
    });
  }

  // ─── Date helpers ───────────────────────────────────────────────────────

  #computeEarliestDate(now) {
    let d = new Date(Date.UTC(now.year, now.month - 1, now.day));
    const offset = now.hour < this.cutoffHour ? 1 : 2;
    d.setUTCDate(d.getUTCDate() + offset);

    while (this.#isClosedDate(d)) {
      d.setUTCDate(d.getUTCDate() + 1);
    }
    return this.#formatYMD(d);
  }

  #isClosedDate(d) {
    if (this.closedWeekdays.includes(d.getUTCDay())) return true;
    if (this.blackoutDates.includes(this.#formatYMD(d))) return true;
    return false;
  }

  #nowInShopTimezone() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: this.shopTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value || '0';
    return {
      year: parseInt(get('year'), 10),
      month: parseInt(get('month'), 10),
      day: parseInt(get('day'), 10),
      hour: parseInt(get('hour'), 10),
      minute: parseInt(get('minute'), 10),
    };
  }

  #parseYMD(value) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!m) return null;
    return new Date(Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)));
  }

  #formatYMD(d) {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  #humanizeDate(ymd) {
    const d = this.#parseYMD(ymd);
    if (!d) return ymd;
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(d);
  }
}

if (!customElements.get('pickup-scheduler')) {
  customElements.define('pickup-scheduler', PickupScheduler);
}
